import React, { useEffect, useRef, useState } from 'react';
import { Phone, Mic, Languages, ShieldCheck, Volume2, Check, Pause, Plus, Loader2, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../hooks/use-toast';
import { api, useAuth } from '../../context/AuthContext';

const LANG_LABEL = { auto: 'Auto-detect English + Spanish', en: 'English only', es: 'Spanish only' };

// Read-and-write endpoint for the primary + purchased additional numbers.
// Rendered under the Realtor Info card so realtors manage everything voice-
// related in one place. Mobile app hits the same endpoint but hides the
// purchase CTA per product requirements.
function AdditionalNumberPurchase() {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const startCheckout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/assistant-numbers/checkout', {});
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast({ title: 'Could not start checkout', variant: 'destructive' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        title: 'Checkout unavailable',
        description: typeof detail === 'string' ? detail : 'Try again in a moment.',
        variant: 'destructive',
      });
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 rounded-lg border border-brand-gold/40 bg-brand-gold/5 p-4"
         data-testid="addl-number-prompt">
      <div className="flex items-start gap-3">
        <Plus className="w-5 h-5 text-brand-gold mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-brand-green">
            Need additional phone numbers for greetings in different languages?
          </div>
          <div className="text-xs text-brand-green/70 mt-1">
            Buy one here for <b>$20/month per additional number</b>. Each purchased number
            gets its own greeting language you can set below.
          </div>
        </div>
        <Button onClick={startCheckout} disabled={busy}
                data-testid="addl-number-buy-btn"
                className="bg-brand-gold text-brand-green hover:bg-brand-gold/90 shrink-0">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</> : 'Buy a number'}
        </Button>
      </div>
    </div>
  );
}

function AdditionalNumbersList({ greetingLangOptions, voiceOptions = [] }) {
  const { toast } = useToast();
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [cancelingId, setCancelingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/assistant-numbers');
      setNumbers(Array.isArray(data) ? data : []);
    } catch (_) { /* non-fatal */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patchNumber = async (row, body, successMessage) => {
    setSavingId(row.id);
    try {
      const { data } = await api.patch(`/assistant-numbers/${row.id}`, body);
      setNumbers((prev) => prev.map((n) => (n.id === row.id ? { ...n, ...data } : n)));
      toast({ title: successMessage });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        title: 'Update failed',
        description: typeof detail === 'string' ? detail : 'Try again.',
        variant: 'destructive',
      });
    } finally { setSavingId(null); }
  };

  const updateLang = (row, code) => patchNumber(row, { greeting_language: code }, 'Greeting language updated');
  const updateVoice = (row, id) => patchNumber(row, { voice_id: id }, 'Voice updated');

  const cancelNumber = async (row) => {
    const ok = window.confirm(
      `Cancel ${row.phone_number}?\n\nThis will end the $20/month subscription for this ` +
      `number and release it back to Twilio. This cannot be undone — you'll need to ` +
      `buy a new number if you want to add one again.`,
    );
    if (!ok) return;
    setCancelingId(row.id);
    try {
      await api.delete(`/assistant-numbers/${row.id}`);
      setNumbers((prev) => prev.filter((n) => n.id !== row.id));
      toast({ title: `Canceled ${row.phone_number}` });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        title: 'Cancel failed',
        description: typeof detail === 'string' ? detail : 'Try again.',
        variant: 'destructive',
      });
    } finally { setCancelingId(null); }
  };

  const langOpts = greetingLangOptions.length
    ? greetingLangOptions
    : [{ code: 'en', label: 'English', native: 'English' }];

  // Voice labels come back from `/settings/voice` as "Emma — Warm & friendly";
  // normalize to the middle-dot style used elsewhere on this page.
  const voiceOpts = (voiceOptions.length ? voiceOptions : []).map((v) => ({
    id: v.id,
    label: (v.label || v.id).replace(' — ', ' · '),
  }));

  // Only render the "additional numbers" section when the user actually
  // has ≥1 add-on beyond the primary — the primary is edited via the
  // Greeting language dropdown in Realtor Info.
  const addlNumbers = numbers.filter((n) => !n.is_primary && n.status === 'active');
  if (loading || addlNumbers.length === 0) return null;

  return (
    <Card className="p-6 space-y-3" data-testid="addl-numbers-list">
      <div className="flex items-start gap-3">
        <Phone className="w-5 h-5 text-brand-gold mt-1" />
        <div>
          <div className="font-serif text-lg text-brand-green">Additional AI numbers</div>
          <p className="text-xs text-brand-green/60 mt-1">
            Each purchased number can have its own greeting language and assistant
            voice. Callers on a Spanish-greeting number will hear the AI open in
            Spanish, then switch to whatever language they speak.
          </p>
        </div>
      </div>
      <div className="space-y-3">
        {addlNumbers.map((n) => {
          const disabled = savingId === n.id || cancelingId === n.id;
          return (
            <div key={n.id} data-testid={`addl-number-${n.id}`}
                 className="rounded-lg border border-brand-green/10 bg-white p-3 flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-brand-green">{n.phone_number}</div>
                <div className="text-[11px] text-brand-green/50 mt-0.5">
                  {n.greeting_language_label} · {n.voice_label}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full lg:w-[28rem]">
                <label className="text-[11px] text-brand-green/70 sm:col-span-1">
                  <span className="block mb-1">Greeting language</span>
                  <select
                    data-testid={`addl-number-lang-${n.id}`}
                    value={n.greeting_language}
                    disabled={disabled}
                    onChange={(e) => updateLang(n, e.target.value)}
                    className="w-full rounded-md border border-brand-green/20 bg-white px-3 py-2 text-sm text-brand-green focus:outline-none focus:ring-2 focus:ring-brand-gold disabled:opacity-60"
                  >
                    {langOpts.map((opt) => (
                      <option key={opt.code} value={opt.code}>{opt.native} · {opt.label}</option>
                    ))}
                  </select>
                </label>
                <label className="text-[11px] text-brand-green/70 sm:col-span-1">
                  <span className="block mb-1">Voice</span>
                  <select
                    data-testid={`addl-number-voice-${n.id}`}
                    value={n.voice_id}
                    disabled={disabled || voiceOpts.length === 0}
                    onChange={(e) => updateVoice(n, e.target.value)}
                    className="w-full rounded-md border border-brand-green/20 bg-white px-3 py-2 text-sm text-brand-green focus:outline-none focus:ring-2 focus:ring-brand-gold disabled:opacity-60"
                  >
                    {voiceOpts.length === 0 ? (
                      <option value={n.voice_id}>{n.voice_label}</option>
                    ) : voiceOpts.map((opt) => (
                      <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => cancelNumber(n)}
                disabled={cancelingId === n.id}
                data-testid={`addl-number-cancel-${n.id}`}
                className="text-red-700 hover:bg-red-50 shrink-0"
              >
                {cancelingId === n.id
                  ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Canceling…</>
                  : <><XCircle className="w-3 h-3 mr-1" /> Cancel</>}
              </Button>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// Live sample preview: fetches the EXACT Gemini voice the AI uses on
// real calls via `/api/voice/sample`, caches to Emergent Object Storage
// server-side, and plays the WAV in a hidden <audio> element. No more
// OS browser TTS — this way the preview character matches the live
// in-call experience 1:1.
const SAMPLE_PART = 'greeting';

export default function CrmSettingsVoice() {
  const { toast } = useToast();
  const { user, refresh: refreshAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [voiceOptions, setVoiceOptions] = useState([]);
  const [langOptions, setLangOptions] = useState([]);
  const [assistantNumber, setAssistantNumber] = useState(null);
  const [form, setForm] = useState({ voice_id: 'Aoede', language: 'auto', ai_answering_enabled: true, recording_enabled: true });
  const [previewingId, setPreviewingId] = useState(null);
  const [previewLoadingId, setPreviewLoadingId] = useState(null);
  const audioRef = useRef(null);

  // Realtor Info — feeds the AI greeting. Kept next to the voice/language
  // pickers so realtors see everything the AI reads in one place.
  const [profileForm, setProfileForm] = useState({ name: '', name_pronunciation: '', brokerage: '', greeting_language: 'en' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [greetingLangOptions, setGreetingLangOptions] = useState([]);
  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || '',
        name_pronunciation: user.name_pronunciation || '',
        brokerage: user.brokerage || '',
        greeting_language: user.greeting_language || 'en',
      });
    }
  }, [user]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/settings/voice');
        if (Array.isArray(data?.greeting_language_options)) {
          setGreetingLangOptions(data.greeting_language_options);
        }
      } catch (_) { /* non-fatal */ }
    })();
  }, []);
  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      await api.patch('/auth/me', profileForm);
      if (typeof refreshAuth === 'function') await refreshAuth();
      toast({ title: 'Realtor info saved',
              description: 'The AI will use this on the next call.' });
    } catch (err) {
      toast({ title: 'Save failed',
              description: err?.response?.data?.detail || 'Try again.',
              variant: 'destructive' });
    } finally { setProfileSaving(false); }
  };

  // Stop any in-flight preview when the user leaves the page or switches tabs.
  useEffect(() => () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (_) { /* no-op */ }
      audioRef.current.src = '';
      audioRef.current = null;
    }
  }, []);

  const stopPreview = () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (_) { /* no-op */ }
    }
    setPreviewingId(null);
  };

  const previewVoice = async (voiceId) => {
    // Tap the same voice twice = stop.
    if (previewingId === voiceId) {
      stopPreview();
      return;
    }
    // Different voice tapped = cancel the in-flight one.
    stopPreview();
    setPreviewLoadingId(voiceId);
    try {
      // Fetch the signed sample URL from the backend (identical endpoint
      // the mobile app uses — same voice character the live caller hears).
      const { data } = await api.get('/voice/sample', {
        params: { voice: voiceId, part: SAMPLE_PART },
      });
      const relPath = data?.url;
      if (!relPath) {
        toast({ title: 'Sample unavailable', variant: 'destructive' });
        setPreviewLoadingId(null);
        return;
      }
      // Build an absolute URL — api base is REACT_APP_BACKEND_URL and
      // the sample is served through /api/files/... (already prefixed
      // by the backend response).
      const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      const absUrl = relPath.startsWith('http') ? relPath : `${base}${relPath}`;

      // Fetch the WAV with credentials so the authed /api/files/* route
      // sees our session cookie, then play it from an in-memory blob —
      // iOS Safari + Chrome both support this and it sidesteps CORS
      // audio-tag quirks across emergentagent.com subdomains.
      const resp = await fetch(absUrl, { credentials: 'include' });
      if (!resp.ok) {
        toast({ title: 'Sample failed to load', description: `HTTP ${resp.status}`,
                variant: 'destructive' });
        setPreviewLoadingId(null);
        return;
      }
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);

      const audio = new Audio(blobUrl);
      audio.onended = () => {
        setPreviewingId((cur) => (cur === voiceId ? null : cur));
        URL.revokeObjectURL(blobUrl);
      };
      audio.onerror = () => {
        toast({ title: 'Playback failed', variant: 'destructive' });
        setPreviewingId((cur) => (cur === voiceId ? null : cur));
        URL.revokeObjectURL(blobUrl);
      };
      audioRef.current = audio;
      setPreviewLoadingId(null);
      setPreviewingId(voiceId);
      await audio.play();
    } catch (err) {
      setPreviewLoadingId(null);
      const detail = err?.response?.data?.detail;
      toast({
        title: 'Preview failed',
        description: typeof detail === 'string' ? detail : (err?.message || 'Try again in a moment.'),
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/settings/voice');
        if (cancelled) return;
        setForm(data.settings);
        setVoiceOptions(data.voice_options || []);
        setLangOptions(data.language_options || []);
        setAssistantNumber(data.assistant_number);
      } catch (e) {
        toast({ title: 'Failed to load voice settings', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [toast]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings/voice', form);
      toast({ title: 'Voice settings saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Unknown', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-brand-green/70">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="voice-settings">
      <div>
        <h2 className="font-serif text-2xl text-brand-green">AI receptionist</h2>
        <p className="text-sm text-brand-green/70 mt-1">
          Your callers reach {assistantNumber || 'an AI assistant that answers under your name'}.
        </p>
      </div>

      <Card className="p-6 space-y-4" data-testid="realtor-info-card">
        <div>
          <div className="font-serif text-lg text-brand-green">Realtor info</div>
          <p className="text-xs text-brand-green/60 mt-1">
            The AI greets every caller with <span className="italic">
            "Good [tod]! This is [Voice], {profileForm.name_pronunciation || (profileForm.name || 'your first name').split(' ')[0]}
            {profileForm.brokerage ? ` with ${profileForm.brokerage}` : ''}. How may I help you today?"</span>
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="ri-name">Full name</Label>
            <Input id="ri-name" data-testid="realtor-info-name" value={profileForm.name}
                   onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                   placeholder="Jane Doe" />
            <p className="text-[11px] text-brand-green/50 mt-1">Only your first name is read to callers.</p>
          </div>
          <div>
            <Label htmlFor="ri-brokerage">Brokerage</Label>
            <Input id="ri-brokerage" data-testid="realtor-info-brokerage" value={profileForm.brokerage}
                   onChange={(e) => setProfileForm({ ...profileForm, brokerage: e.target.value })}
                   placeholder="Keller Williams" />
          </div>
        </div>
        <div>
          <Label htmlFor="ri-pron">Name pronunciation (optional)</Label>
          <Input id="ri-pron" data-testid="realtor-info-pron" value={profileForm.name_pronunciation}
                 onChange={(e) => setProfileForm({ ...profileForm, name_pronunciation: e.target.value })}
                 placeholder="e.g. 'jayn doh' for Jayne Doughty" />
          <p className="text-[11px] text-brand-green/50 mt-1">Phonetic respelling drives how the AI voices your name.</p>
        </div>
        <div>
          <Label htmlFor="ri-greeting-lang">Greeting language</Label>
          <select
            id="ri-greeting-lang"
            data-testid="realtor-info-greeting-lang"
            value={profileForm.greeting_language}
            onChange={(e) => setProfileForm({ ...profileForm, greeting_language: e.target.value })}
            className="mt-1 block w-full rounded-md border border-brand-green/20 bg-white px-3 py-2 text-sm text-brand-green focus:outline-none focus:ring-2 focus:ring-brand-gold"
          >
            {(greetingLangOptions.length ? greetingLangOptions : [{ code: 'en', label: 'English', native: 'English' }]).map((opt) => (
              <option key={opt.code} value={opt.code}>
                {opt.native} · {opt.label}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-brand-green/50 mt-1">
            The first sentence of every call opens in this language. Gemini Live still
            auto-detects and switches to the caller’s language mid-call.
          </p>

          <AdditionalNumberPurchase greetingLangOptions={greetingLangOptions} />
        </div>
        <div>
          <Button onClick={saveProfile} disabled={profileSaving}
                  data-testid="realtor-info-save"
                  className="bg-brand-green text-brand-cream hover:bg-brand-green/90">
            {profileSaving ? 'Saving…' : 'Save realtor info'}
          </Button>
        </div>
      </Card>

      <AdditionalNumbersList greetingLangOptions={greetingLangOptions} voiceOptions={voiceOptions} />

      <Card className="p-6 space-y-6">
        <div className="flex items-start gap-4">
          <Phone className="w-5 h-5 text-brand-gold mt-1" />
          <div className="flex-1">
            <div className="text-sm font-medium text-brand-green">Your assistant number</div>
            <div className="font-mono text-lg text-brand-green mt-1">{assistantNumber || 'Not provisioned yet'}</div>
            <p className="text-xs text-brand-green/60 mt-1">Provisioned automatically once your trial activates. Call it to test the AI.</p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-brand-gold mt-1" />
            <div>
              <Label htmlFor="ai-enabled">AI answers when you don&rsquo;t</Label>
              <p className="text-xs text-brand-green/60 mt-1">Off means callers hear the record-a-message fallback.</p>
            </div>
          </div>
          <Switch
            id="ai-enabled"
            data-testid="voice-ai-enabled"
            checked={form.ai_answering_enabled}
            onCheckedChange={(v) => setForm({ ...form, ai_answering_enabled: v })}
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Mic className="w-5 h-5 text-brand-gold mt-1" />
            <div>
              <Label htmlFor="rec-enabled">Record calls</Label>
              <p className="text-xs text-brand-green/60 mt-1">Transcripts are always saved; audio recordings help review calls later.</p>
            </div>
          </div>
          <Switch
            id="rec-enabled"
            data-testid="voice-recording-enabled"
            checked={form.recording_enabled}
            onCheckedChange={(v) => setForm({ ...form, recording_enabled: v })}
          />
        </div>

        <div>
          <div className="flex items-start gap-3 mb-2">
            <Languages className="w-5 h-5 text-brand-gold mt-1" />
            <Label htmlFor="lang">Language</Label>
          </div>
          <select
            id="lang"
            data-testid="voice-language"
            value={form.language}
            onChange={(e) => setForm({ ...form, language: e.target.value })}
            className="w-full rounded-lg border border-brand-green/20 bg-white px-3 py-2 text-sm"
          >
            {langOptions.map((l) => <option key={l} value={l}>{LANG_LABEL[l] || l}</option>)}
          </select>
        </div>

        <div>
          <Label>Assistant voice</Label>
          <p className="text-xs text-brand-green/60 mt-1">
            Tap a card to pick it. The preview button plays a rough browser
            sample — the actual call voice is a rich Gemini Live voice.
          </p>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3"
            role="radiogroup"
            aria-label="Assistant voice"
            data-testid="voice-card-grid"
          >
            {voiceOptions.map((v) => {
              const isSelected = form.voice_id === v.id;
              const isPreviewing = previewingId === v.id;
              const isLoading = previewLoadingId === v.id;
              // Split "Emma — Warm & friendly" into name + subtitle.
              const [name, subtitle] = v.label.split('—').map((s) => s.trim());
              return (
                <button
                  type="button"
                  key={v.id}
                  role="radio"
                  aria-checked={isSelected}
                  data-testid={`voice-card-${v.id}`}
                  onClick={() => setForm({ ...form, voice_id: v.id })}
                  className={`text-left rounded-xl border p-4 transition-all flex items-start gap-3 ${
                    isSelected
                      ? 'border-brand-green bg-brand-green/5 shadow-sm'
                      : 'border-brand-green/20 bg-white hover:border-brand-green/40 hover:bg-brand-cream/30'
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'bg-brand-green text-brand-cream' : 'bg-brand-cream text-brand-green'
                    }`}
                  >
                    <Volume2 className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-semibold text-brand-green truncate">{name}</div>
                      {isSelected && (
                        <Check className="w-4 h-4 text-brand-green flex-shrink-0" aria-label="selected" />
                      )}
                    </div>
                    {subtitle && (
                      <div className="text-xs text-brand-green/60 mt-0.5 truncate">{subtitle}</div>
                    )}
                    <span
                      role="button"
                      tabIndex={0}
                      data-testid={`voice-preview-${v.id}`}
                      onClick={(e) => { e.stopPropagation(); previewVoice(v.id); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          previewVoice(v.id);
                        }
                      }}
                      className="inline-flex items-center gap-1.5 text-xs mt-2 px-2 py-1 rounded-md border border-brand-green/20 text-brand-green hover:bg-brand-cream cursor-pointer"
                    >
                      {isLoading ? (
                        <><Loader2 className="w-3 h-3 animate-spin" /> Loading…</>
                      ) : isPreviewing ? (
                        <><Pause className="w-3 h-3" /> Stop</>
                      ) : (
                        <><Volume2 className="w-3 h-3" /> Preview</>
                      )}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <Button onClick={save} disabled={saving} data-testid="voice-save"
                className="bg-brand-green text-brand-cream hover:bg-brand-green/90">
          {saving ? 'Saving…' : 'Save settings'}
        </Button>
      </Card>
    </div>
  );
}
